/*!
 * Deep merge two or more objects or arrays.
 * (c) 2023 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param   {*} ...objs  The arrays or objects to merge
 * @returns {*}          The merged arrays or objects
 */
export function deepmerge(...objs: object[]) {
  /**
   * Get the object type
   * @param  {*}       obj The object
   * @return {String}      The object type
   */
  function getType(obj: object): string {
    return Object.prototype.toString.call(obj).slice(8, -1).toLowerCase();
  }

  /**
   * Deep merge two objects
   * @return {Object}
   */
  function mergeObj(clone: object, obj: object) {
    for (const [key, value] of Object.entries(obj)) {
      const type = getType(value);
      if (
        clone[key] !== undefined &&
        getType(clone[key]) === type &&
        ['array', 'object'].includes(type)
      ) {
        clone[key] = deepmerge(clone[key], value);
      } else {
        clone[key] = structuredClone(value);
      }
    }
  }

  // Create a clone of the first item in the objs array
  let clone: object | undefined = structuredClone(objs.shift());

  // Loop through each item
  for (const obj of objs) {
    // Get the object type
    const type = getType(obj);

    // If the current item isn't the same type as the clone, replace it
    if (clone !== undefined && getType(clone) !== type) {
      clone = structuredClone(obj);
      continue;
    }

    // Otherwise, merge
    if (type === 'array') {
      clone = [...(<[]>clone), ...structuredClone(<[]>obj)];
    } else if (type === 'object' && clone != undefined) {
      mergeObj(clone, obj);
    } else {
      clone = obj;
    }
  }

  return clone;
}
